/**
 * Single source of truth for payment methods, mirroring the backend
 * PaymentMethod enum (api/app/models.py). The type is derived from the
 * value list below, not hand-declared separately — add a method here once
 * (value, label, color) and it's correct everywhere, with no second list
 * to remember to update. Each curated list further down narrows this to a
 * context — not every method is meaningful everywhere:
 *   - COD (cash a courier collects from a customer and later remits to us)
 *     only applies to money coming IN on a delivery-tracked sales order.
 *   - STORE_CREDIT is a customer-facing sales concept.
 */
const PAYMENT_METHOD_VALUES = [
    "CASH",
    "CARD",
    "BKASH",
    "NAGAD",
    "ROCKET",
    "BANK_TRANSFER",
    "COD",
    "STORE_CREDIT",
] as const;

export type PaymentMethod = (typeof PAYMENT_METHOD_VALUES)[number];

export const ALL_PAYMENT_METHODS: PaymentMethod[] = [...PAYMENT_METHOD_VALUES];

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
    CASH: "Cash",
    CARD: "Card",
    BKASH: "bKash",
    NAGAD: "Nagad",
    ROCKET: "Rocket",
    BANK_TRANSFER: "Bank Transfer",
    COD: "Cash on Delivery (COD)",
    STORE_CREDIT: "Store Credit",
};

// MUI Chip's `color` prop values actually used below.
export type PaymentMethodColor =
    | "success"
    | "primary"
    | "secondary"
    | "info"
    | "warning"
    | "default";

// Single shared color mapping — previously duplicated (and already
// disagreeing) between PaymentManagement.tsx and ExpenseList.tsx.
const PAYMENT_METHOD_COLORS: Record<PaymentMethod, PaymentMethodColor> = {
    CASH: "success",
    CARD: "primary",
    BKASH: "secondary",
    NAGAD: "secondary",
    ROCKET: "secondary",
    BANK_TRANSFER: "info",
    COD: "warning",
    STORE_CREDIT: "warning",
};

export const getPaymentMethodColor = (
    method: PaymentMethod | string,
): PaymentMethodColor =>
    PAYMENT_METHOD_COLORS[method as PaymentMethod] ?? "default";

// Sales orders recorded/managed by staff (order drawer, order form) — these
// track courier-delivered orders, so COD is a real, expected method here.
export const ORDER_PAYMENT_METHODS: PaymentMethod[] = ALL_PAYMENT_METHODS;

// POS — in-person checkout at the counter. No courier is involved, so COD
// doesn't apply, but a walk-in customer can still redeem store credit.
export const POS_PAYMENT_METHODS: PaymentMethod[] = ALL_PAYMENT_METHODS.filter(
    (m) => m !== "COD",
);

// Purchase (supplier) payments — money going OUT to a supplier. COD is money
// a courier collects from OUR customers, and STORE_CREDIT is a sales
// concept — neither applies to paying a supplier.
export const PURCHASE_PAYMENT_METHODS: PaymentMethod[] =
    ALL_PAYMENT_METHODS.filter((m) => m !== "COD" && m !== "STORE_CREDIT");

// Business expenses (rent, utilities, ...) — same COD reasoning as
// purchases, but STORE_CREDIT stays available here: it's how giving away
// inventory outside a normal sale (e.g. feeding stray cats) gets recorded
// — an order for the item paid via STORE_CREDIT, offset by a matching
// expense, also via STORE_CREDIT, tagged with its own expense category so
// it's visible in category-wise reporting without touching the customer
// "gift item" stats on orders.
export const EXPENSE_PAYMENT_METHODS: PaymentMethod[] =
    ALL_PAYMENT_METHODS.filter((m) => m !== "COD");

// Asset purchases (equipment, furniture, vehicles, ...) — money going OUT
// to buy something for the business, same reasoning as purchases/expenses.
export const ASSET_PAYMENT_METHODS: PaymentMethod[] =
    ALL_PAYMENT_METHODS.filter((m) => m !== "COD" && m !== "STORE_CREDIT");

// Customer return refunds — cash/manual money going back OUT for a
// returned item. BKASH is excluded on purpose: an actual bKash-gateway
// refund has to go through the order's own Refund button (a real API
// call), not this generic path. COD doesn't apply either — that's money a
// courier collects from the customer on delivery, not something staff
// hand back.
export const RETURN_REFUND_PAYMENT_METHODS: PaymentMethod[] =
    ALL_PAYMENT_METHODS.filter((m) => m !== "COD" && m !== "BKASH");
